import { Loader2, Search, X } from 'lucide-react';
import { useForm } from 'react-hook-form';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import toast from 'react-hot-toast';
import { settingsService } from '../../services/settings.service';
import type { SearchConfigInput } from '../../services/settings.service';

interface SearchIntegrationFormProps {
  onClose: () => void;
}

interface FormData {
  braveApiKey: string;
  serperApiKey: string;
}

export function SearchIntegrationForm({ onClose }: SearchIntegrationFormProps) {
  const qc = useQueryClient();

  const { data: searchConfig, isLoading } = useQuery({
    queryKey: ['settings', 'search'],
    queryFn: () => settingsService.getSearch(),
  });

  const { register, handleSubmit } = useForm<FormData>({
    defaultValues: { braveApiKey: '', serperApiKey: '' },
  });

  const saveMutation = useMutation({
    mutationFn: (data: SearchConfigInput) => settingsService.saveSearch(data),
    onSuccess: () => {
      toast.success('Configuração de busca salva');
      qc.invalidateQueries({ queryKey: ['settings', 'search'] });
      onClose();
    },
    onError: () => toast.error('Erro ao salvar configuração de busca'),
  });

  const removeMutation = useMutation({
    mutationFn: (key: 'brave' | 'serper') =>
      settingsService.saveSearch(key === 'brave' ? { braveApiKey: '' } : { serperApiKey: '' }),
    onSuccess: () => {
      toast.success('Chave removida');
      qc.invalidateQueries({ queryKey: ['settings', 'search'] });
    },
    onError: () => toast.error('Erro ao remover a chave'),
  });

  async function onSubmit(data: FormData) {
    const payload: SearchConfigInput = {};
    // Campo em branco significa "nao mexer". Para apagar uma chave existe o
    // botao Remover, que envia '' explicitamente.
    if (data.braveApiKey.trim()) {
      payload.braveApiKey = data.braveApiKey.trim();
    }
    if (data.serperApiKey.trim()) {
      payload.serperApiKey = data.serperApiKey.trim();
    }
    await saveMutation.mutateAsync(payload);
  }

  return (
    <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
      <div className="bg-surface rounded-xl shadow-xl w-full max-w-lg max-h-[90vh] overflow-y-auto">
        <div className="flex items-center justify-between p-5 border-b border-border sticky top-0 bg-surface">
          <div className="flex items-center gap-2">
            <Search className="w-5 h-5 text-ink-primary" />
            <h2 className="text-lg font-semibold text-ink-primary">Busca (Skool)</h2>
          </div>
          <button onClick={onClose} className="p-1 rounded hover:bg-subtle">
            <X className="w-5 h-5 text-ink-tertiary" />
          </button>
        </div>

        {isLoading ? (
          <div className="flex justify-center py-12">
            <Loader2 className="w-6 h-6 animate-spin text-ink-primary" />
          </div>
        ) : (
          <form onSubmit={handleSubmit(onSubmit)} className="p-5 space-y-4">
            <p className="text-sm text-ink-tertiary">
              As chaves ficam cifradas no banco, não em variáveis de ambiente. Depois de salvas elas
              nunca voltam para o navegador — para trocar, digite a nova; para apagar, use Remover.
            </p>

            <div className="space-y-2 border border-border rounded-lg p-4">
              <div className="flex items-center justify-between">
                <label className="block text-sm font-medium text-ink-secondary">
                  Brave Search API Key
                </label>
                <span className="text-xs text-ink-tertiary">
                  {searchConfig?.hasBraveKey ? 'Chave gravada' : 'Não configurada'}
                </span>
              </div>
              <input
                type="password"
                autoComplete="off"
                placeholder={
                  searchConfig?.hasBraveKey ? 'Chave gravada — preencha para substituir' : 'BSA...'
                }
                {...register('braveApiKey')}
                className="w-full px-3 py-2 border border-border-strong rounded-lg text-sm font-mono focus:outline-none focus:ring-2 focus-visible:ring-accent focus:border-transparent"
              />
              <div className="flex items-center justify-between">
                <a
                  href="https://brave.com/search/api/"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="text-xs underline text-accent"
                >
                  brave.com/search/api
                </a>
                <button
                  type="button"
                  onClick={() => removeMutation.mutate('brave')}
                  disabled={!searchConfig?.hasBraveKey || removeMutation.isPending}
                  className="px-3 py-1.5 text-xs font-medium text-danger hover:bg-subtle rounded-lg disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
                >
                  Remover
                </button>
              </div>
            </div>

            <div className="space-y-2 border border-border rounded-lg p-4">
              <div className="flex items-center justify-between">
                <label className="block text-sm font-medium text-ink-secondary">
                  Serper API Key
                </label>
                <span className="text-xs text-ink-tertiary">
                  {searchConfig?.hasSerperKey ? 'Chave gravada' : 'Não configurada'}
                </span>
              </div>
              <input
                type="password"
                autoComplete="off"
                placeholder={
                  searchConfig?.hasSerperKey
                    ? 'Chave gravada — preencha para substituir'
                    : 'chave do serper.dev'
                }
                {...register('serperApiKey')}
                className="w-full px-3 py-2 border border-border-strong rounded-lg text-sm font-mono focus:outline-none focus:ring-2 focus-visible:ring-accent focus:border-transparent"
              />
              <div className="flex items-center justify-between">
                <a
                  href="https://serper.dev/api-key"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="text-xs underline text-accent"
                >
                  serper.dev/api-key
                </a>
                <button
                  type="button"
                  onClick={() => removeMutation.mutate('serper')}
                  disabled={!searchConfig?.hasSerperKey || removeMutation.isPending}
                  className="px-3 py-1.5 text-xs font-medium text-danger hover:bg-subtle rounded-lg disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
                >
                  Remover
                </button>
              </div>
            </div>

            <div className="flex items-center justify-end gap-2 pt-2 border-t border-border">
              <button
                type="button"
                onClick={onClose}
                className="px-4 py-2 text-sm font-medium text-ink-secondary hover:bg-subtle rounded-lg transition-colors"
              >
                Cancelar
              </button>
              <button
                type="submit"
                disabled={saveMutation.isPending}
                className="flex items-center gap-2 px-4 py-2 text-sm font-medium text-canvas bg-ink-primary rounded-lg hover:bg-ink-secondary disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
              >
                {saveMutation.isPending && <Loader2 className="w-4 h-4 animate-spin" />}
                Salvar
              </button>
            </div>
          </form>
        )}
      </div>
    </div>
  );
}
